import {values} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'

import {
    OrderChannel,
    EasyBookingGroupSaleChannelType,
    OrderEntry,
    OrderEntryType,
    PageSet,
    Entry,
} from '@web/core/constants'
import {PAYMENT_ROUTES, PAYMENT_METHODS_ROUTES, ROUTER_ORDER_COUPON} from '@web/core/routes'
import {LanguageType} from '@web/i18n'

const EasyBookings = [OrderChannel.EASY_BOOKING, OrderChannel.LOCAL_PAY, OrderChannel.CAFE, OrderChannel.BLOG].join('|')

/**
 * @deprecated common slice utils 로 이관
 */
const PathnameRegexp = {
    ORDER_SHEET_APP: /^\/(order|ordersheet)\//,
    ORDER: new RegExp(`/order/checkout(?:/(${values(OrderChannel).join('|')}))?/`),
    ORDER_SHEET_CHECKOUT: new RegExp(`/ordersheet(?:/(${values(OrderChannel).join('|')}))?/`),
    MALL_CHECKOUT: /\/ordersheet\/mall\//,
    ORDER_MALL_CHECKOUT: /\/order\/(checkout|apply|fail)\/mall\//,
    FLEA_MARKET_CHECKOUT: /\/ordersheet\/fleamarket/,
    MALL_BRIDGE_CHECKOUT: /^\/order\/bridge\/mall(\/.*)?$/,
    EASY_BOOKING_BRIDGE_CHECKOUT: new RegExp(`/order/bridge(?:/(${EasyBookings}))?/`),
    EASY_BOOKING_CHECKOUT: new RegExp(`/ordersheet/(?:/(${EasyBookings}))?/`),
    ORDER_EASY_BOOKING_CHECKOUT: new RegExp(`/order/(checkout|apply|fail)(?:/(${EasyBookings}))?/`),
    FAMILY_PAY_CHECKOUT: /\/ordersheet\/familyPay(?:\/(?!result))/,
    ORDER_RESULT: new RegExp(`/order/result(?:/(${values(OrderChannel).join('|')}))?/`),
    MALL_RESULT: new RegExp(`/order/result/${OrderChannel.MALL}?/`),
    EASY_BOOKING_RESULT: /\/order\/result\/easybooking\//,
    LOCAL_PAY_RESULT: /\/order\/result\/localpay\//,
    BLOG_RESULT: /\/order\/result\/blog\//,
    FLEA_MARKET_RESULT: /\/order\/result\/fleamarket\//,
    SELLER_RESULT: new RegExp(`/order/result/${OrderChannel.SELLER}?/`),
    BOOKING: /\/order\/paymentMethod\/booking?/,
    SUBSCRIPTION: /\/ordersheet\/subscription\/?/,
    /** TODO(패밀리결제): coupon도 pageSet으로 분리 */
    SUBSCRIPTION_MANAGEMENT: /\/order\/coupon\/subscription?/,
    /*
    /**
     * C2 주문서는 /ordersheet/seller 뒤에 추가 path가 붙는 경우 모두 orderSheetId가 아니므로 제외 추가처리 했습니다.
     * path가 추가될때마다 이 정규식이 변경되어야하므로 추후 개선과제로 개선 진행하면 좋을 것 같습니다.
     */
    SELLER: new RegExp(`/ordersheet/seller(?:/(?!${['fail', 'result', 'apply', 'popup', 'fds'].join('|')}))`),
    SELLER_CHECKOUT: /\/ordersheet\/seller(?:\/(?!result))/,
    ORDER_SHEET_SELLER_RESULT: /\/ordersheet\/seller\/result\/?/,
    DICON_BUYING: /\/digitalContents\/buy\/checkout\//,
    DICON_SUBSCRIBE: /\/paymentMethods\/digitalcontents\/(?!(result|bridge))/,
    PARTNERS: /\/instantPay\/partners\//,
    PARTNERS_AUTO: /\/paymentMethods\/partners\//,
    DELIVERY_MANAGER: /\/order\/delivery\/(?!tracking)/,
    DELIVERY_MANAGER_CHANGE: /\/order\/delivery\/(orderResult|scheduledOrderResult)\//,
    ORDER_DETAIL: /\/order\/status\//,
    PURCHASE_CONFIRMATION: /\/order\/purchaseDecision\//,
    // eslint-disable-next-line prefer-regex-literals
    DETAIL: new RegExp(`/order/status(?:/(popupBridge))?/`),
    RESULT: new RegExp(`/order/result(?:/(${Object.values(OrderChannel).join('|')}))?/|/ordersheet/familyPay/result/`),
    PAYMENT_DETAIL: /\/instantPay\/detail\//,
    RECEIVE_POINT: /\/instantPay\/receivePoint\//,
    /** 결제수단 변경창 Page path */
    PAYMENT_METHOD_MANAGEMENT: /\/order\/paymentMethod\/?/,
    SALE_HISTORY: /^\/order\/sale\/history(\/)?$/,
    SALE_HISTORY_DETAIL: /^\/order\/sale\/history\/\d+$/,
    SALE_SETTLEMENT: /^\/order\/sale\/settlement(\/)?$/,
    SALE_SETTLEMENT_DETAIL: /^\/order\/sale\/settlement\/\d+$/,
    NPAY_BIZ_PAYMENT_CHECKOUT: /^\/payments\/checkout?/,
    INVOICE: /\/payments\/invoice\//,
}

export const getPathNameInfo = (
    pathname: string,
): Record<
    | 'isOrderPathname'
    | 'isSellerPathname'
    | 'isDiconBuyingPathname'
    | 'isDiconAutoPathname'
    | 'isPartnersAutoPathname'
    | 'isOrderSheetPathname'
    | 'isOrderSheetCheckoutPathname'
    | 'isPaymentMethodsPathname'
    | 'isDetailPathName'
    | 'isResultPathName'
    | 'isDeliveryManagerPathName'
    | 'isMallCheckout'
    | 'isEasyBookCheckout'
    | 'isSellerCheckout'
    | 'isFamilyPayCheckout'
    | 'isOrderResult'
    | 'isMallResult'
    | 'isEasyBookResult'
    | 'isSellerResult'
    | 'isOrderSheetSellerResult'
    | 'isOrderSheetApp'
    | 'isOrderDetail'
    | 'isPurchaseConfirmation'
    | 'isDiconAuto'
    | 'isPartnersAuto'
    | 'isPartnersPathname'
    | 'isPaymentDetail'
    | 'isReceivePoint'
    | 'isShoppingSubscription'
    | 'isNaverFinancialAuto'
    | 'isNaverFinancialPayment'
    | 'isCoupon'
    | 'isDeliveryManagerChange'
    | 'isSubscription'
    | 'isSubscriptionManagement'
    | 'isSaleHistory'
    | 'isSaleHistoryDetail'
    | 'isSaleSettlement'
    | 'isSaleSettlementDetail'
    | 'isPaymentMethodManagement'
    | 'isLocalPayResult'
    | 'isBlogResult'
    | 'isNpayBizPaymentCheckout'
    | 'isFleaMarketResult'
    | 'isInvoice',
    () => boolean
> => {
    const isOrderPathname = () => PathnameRegexp.ORDER.test(pathname)
    const isSellerPathname = () => PathnameRegexp.SELLER.test(pathname)
    const isDiconBuyingPathname = () => PathnameRegexp.DICON_BUYING.test(pathname)
    const isDiconAutoPathname = () => PathnameRegexp.DICON_SUBSCRIBE.test(pathname)
    const isPartnersPathname = () => PathnameRegexp.PARTNERS.test(pathname)
    const isPartnersAutoPathname = () => PathnameRegexp.PARTNERS_AUTO.test(pathname)
    const isOrderSheetCheckoutPathname = () => PathnameRegexp.ORDER_SHEET_CHECKOUT.test(pathname)
    const isPaymentDetail = () => PathnameRegexp.PAYMENT_DETAIL.test(pathname)
    /** 정기구독 신청 flow */
    const isSubscription = () => PathnameRegexp.SUBSCRIPTION.test(pathname)
    /** 정기구독 결제수단 변경 */
    const isSubscriptionManagement = () => PathnameRegexp.SUBSCRIPTION_MANAGEMENT.test(pathname)
    /**
     * 정기구독 신청 + 결제수단 변경 모두 pageset이 PageSet.Subscription
     * 패밀리결제 2차 작업을 통해 결제수단 변경창은 pageset이 paymentMethodManagement로 변경될 예정
     */
    const isShoppingSubscription = () => {
        return isSubscription() || isSubscriptionManagement()
    }

    const isNpayBizPaymentCheckout = () => PathnameRegexp.NPAY_BIZ_PAYMENT_CHECKOUT.test(pathname)

    const isInvoice = () => PathnameRegexp.INVOICE.test(pathname)

    return {
        isOrderPathname,
        isSellerPathname,
        isDiconBuyingPathname,
        isDiconAutoPathname,
        isPartnersAutoPathname,
        isPartnersPathname,
        isOrderSheetCheckoutPathname,
        isOrderSheetPathname: () => isOrderPathname() || isSellerPathname() || isOrderSheetCheckoutPathname(),
        isPaymentMethodsPathname: () => isDiconAutoPathname() || isPartnersAutoPathname(),
        isDetailPathName: () => PathnameRegexp.DETAIL.test(pathname),
        isResultPathName: () => PathnameRegexp.RESULT.test(pathname),
        isDeliveryManagerPathName: () => PathnameRegexp.DELIVERY_MANAGER.test(pathname),
        isMallCheckout: () =>
            PathnameRegexp.MALL_CHECKOUT.test(pathname) ||
            PathnameRegexp.ORDER_MALL_CHECKOUT.test(pathname) ||
            PathnameRegexp.MALL_BRIDGE_CHECKOUT.test(pathname),
        isEasyBookCheckout: () =>
            PathnameRegexp.FLEA_MARKET_CHECKOUT.test(pathname) ||
            PathnameRegexp.EASY_BOOKING_CHECKOUT.test(pathname) ||
            PathnameRegexp.ORDER_EASY_BOOKING_CHECKOUT.test(pathname) ||
            PathnameRegexp.EASY_BOOKING_BRIDGE_CHECKOUT.test(pathname),
        isSellerCheckout: () => PathnameRegexp.SELLER_CHECKOUT.test(pathname),
        isFamilyPayCheckout: () => PathnameRegexp.FAMILY_PAY_CHECKOUT.test(pathname),
        isOrderResult: () => PathnameRegexp.ORDER_RESULT.test(pathname),
        isMallResult: () => PathnameRegexp.MALL_RESULT.test(pathname),
        isEasyBookResult: () => PathnameRegexp.EASY_BOOKING_RESULT.test(pathname),
        isLocalPayResult: () => PathnameRegexp.LOCAL_PAY_RESULT.test(pathname),
        isFleaMarketResult: () => PathnameRegexp.FLEA_MARKET_RESULT.test(pathname),
        isBlogResult: () => PathnameRegexp.BLOG_RESULT.test(pathname),
        isSellerResult: () =>
            PathnameRegexp.SELLER_RESULT.test(pathname) || PathnameRegexp.ORDER_SHEET_SELLER_RESULT.test(pathname),
        isOrderSheetSellerResult: () => PathnameRegexp.ORDER_SHEET_SELLER_RESULT.test(pathname),
        isOrderSheetApp: () => PathnameRegexp.ORDER_SHEET_APP.test(pathname),
        isOrderDetail: () => PathnameRegexp.ORDER_DETAIL.test(pathname),
        isPurchaseConfirmation: () => PathnameRegexp.PURCHASE_CONFIRMATION.test(pathname),
        isDiconAuto: () => pathname.startsWith(PAYMENT_METHODS_ROUTES.DICON_AUTO.MAIN),
        isPartnersAuto: () => pathname.startsWith(PAYMENT_ROUTES.PARTNERS_AUTO.MAIN),
        isPaymentDetail,
        isReceivePoint: () => PathnameRegexp.RECEIVE_POINT.test(pathname),
        isSubscription,
        isSubscriptionManagement,
        isShoppingSubscription,
        isNaverFinancialAuto: () => pathname.startsWith(PAYMENT_ROUTES.NAVER_FINANCIAL_AUTO.MAIN),
        isNaverFinancialPayment: () => pathname.startsWith(PAYMENT_ROUTES.NAVER_FINANCIAL_PAYMENT.MAIN),
        isCoupon: () => pathname.startsWith(ROUTER_ORDER_COUPON.ROOT.getPath()),
        isDeliveryManagerChange: () => PathnameRegexp.DELIVERY_MANAGER_CHANGE.test(pathname),
        isPaymentMethodManagement: () => PathnameRegexp.PAYMENT_METHOD_MANAGEMENT.test(pathname),
        isSaleHistory: () => PathnameRegexp.SALE_HISTORY.test(pathname),
        isSaleHistoryDetail: () => PathnameRegexp.SALE_HISTORY_DETAIL.test(pathname),
        isSaleSettlement: () => PathnameRegexp.SALE_SETTLEMENT.test(pathname),
        isSaleSettlementDetail: () => PathnameRegexp.SALE_SETTLEMENT_DETAIL.test(pathname),
        isNpayBizPaymentCheckout,
        isInvoice,
    }
}

/**
 * API 응답을 받기전 pathname을 가지고 바로 orderSheetId를 가져오는 함수
 * @deprecated common slice utils 로 이관
 */
export const getOrderSheetId = (pathname: string = window.location.pathname) => {
    const {
        isOrderSheetCheckoutPathname,
        isOrderPathname,
        isSellerPathname,
        isDiconBuyingPathname,
        isDiconAutoPathname,
        isPartnersAutoPathname,
        isSubscription,
        isNpayBizPaymentCheckout,
    } = getPathNameInfo(pathname)

    if (isOrderSheetCheckoutPathname()) {
        return pathname.replace(PathnameRegexp.ORDER_SHEET_CHECKOUT, '').split('/')[0] ?? ''
    } else if (isOrderPathname()) {
        return pathname.replace(PathnameRegexp.ORDER, '').split('/')[0] ?? ''
    } else if (isSellerPathname()) {
        return pathname.replace(PathnameRegexp.SELLER, '').split('/')[0] ?? ''
    } else if (isDiconBuyingPathname()) {
        return pathname.replace(PathnameRegexp.DICON_BUYING, '')
    } else if (isDiconAutoPathname()) {
        return pathname.replace(PathnameRegexp.DICON_SUBSCRIBE, '')
    } else if (isPartnersAutoPathname()) {
        return pathname.replace(PathnameRegexp.PARTNERS_AUTO, '')
    } else if (isSubscription()) {
        return pathname.replace(PathnameRegexp.SUBSCRIPTION, '').split('/')[1] ?? ''
    } else if (isNpayBizPaymentCheckout()) {
        return pathname.replace(PathnameRegexp.NPAY_BIZ_PAYMENT_CHECKOUT, '').split('/')[1] ?? ''
    } else {
        return ''
    }
}

/**
 * 주문완료/주문상세/배송지변경창 인 경우 orderNo를 구하여 반환
 * @deprecated common slice utils 로 이관
 */
export const getOrderNo = (location: Location = window.location) => {
    const {
        isDetailPathName,
        isResultPathName,
        isDeliveryManagerPathName,
        isCoupon,
        isDeliveryManagerChange,
        isReceivePoint,
    } = getPathNameInfo(location.pathname)

    if (isDetailPathName()) {
        return location.pathname.replace(PathnameRegexp.DETAIL, '')
    } else if (isResultPathName()) {
        return location.pathname.replace(PathnameRegexp.RESULT, '')
    } else if (isDeliveryManagerChange()) {
        return location.pathname.replace(PathnameRegexp.DELIVERY_MANAGER_CHANGE, '')
    } else if (isDeliveryManagerPathName()) {
        return location.pathname.replace(PathnameRegexp.DELIVERY_MANAGER, '')
    } else if (isCoupon()) {
        const segments = location.pathname.split('/')
        return segments[segments.length - 1] ?? ''
    } else if (isReceivePoint()) {
        return location.pathname.replace(PathnameRegexp.RECEIVE_POINT, '')
    } else {
        return ''
    }
}

/**
 * @deprecated common slice utils 로 이관
 */
export const getSeqId = (location: Location = window.location) => {
    return getOrderSheetId(location.pathname) || getOrderNo(location)
}

/**
 * 주문서 주소값 저장
 * @deprecated common slice utils 로 이관
 */
export const getOrderSheetUrl = (location: Location = window.location) => {
    return location.toString()
}

/** URL query로부터 언어정보 반환 */
export const getLanguage = (location: Location = window.location): LanguageType => {
    const query = qs.parse(location.search)
    return query['language'] ?? 'ko'
}

/**
 * channel 정보로 변환
 * @deprecated common slice utils 로 교체
 */
export const convertChannelType = (channel: string): OrderEntryType => {
    switch (channel) {
        case 'mall':
            return OrderEntry.CHECKOUT
        case 'easybooking':
            return OrderEntry.EASY_BOOKING
        case 'localpay':
            // v2 : tableOrder 포함
            return OrderEntry.LOCAL_PAY
        case 'cafe':
            return OrderEntry.NAVER_CAFE
        case 'fleamarket':
            return OrderEntry.FLEA_MARKET
        case 'blog':
            return OrderEntry.NAVER_BLOG
        default:
            return Entry.ETC
    }
}

/**
 * 예로블카 api response의 응답값을 channel 정보로 변환
 * @deprecated common slice utils 로 교체
 */
export const convertEasyBooingApiResponseChannelType = (channel: EasyBookingGroupSaleChannelType) => {
    switch (channel) {
        case 'EASYBOOKING':
            return OrderEntry.EASY_BOOKING
        case 'LOCALPAY':
            // v2 : tableOrder 포함
            return OrderEntry.LOCAL_PAY
        case 'NAVERBLOG':
            return OrderEntry.NAVER_BLOG
        case 'FLEAMARKET':
            return OrderEntry.FLEA_MARKET
        default:
            return null
    }
}

/**
 * url param 으로부터 channel 정보를 가져오는 함수
 */
export const getChannelType = (value?: Location): OrderEntryType => {
    const location = value || window.location
    const urlRegex = /\/order\/(checkout|result|fail)\/(\w+)/
    /** 변경될 URL 패턴 */
    const nextUrlRegex = /\/ordersheet\/(\w+)/
    const instantPayRegex = /\/instantPay\/(\w+)/

    const {pathname} = location

    const channel =
        urlRegex.exec(pathname)?.[2] ?? nextUrlRegex.exec(pathname)?.[1] ?? instantPayRegex.exec(pathname)?.[1] ?? ''

    return convertChannelType(channel)
}

/**
 * @deprecated - result slice 에서만 사용됨
 **/
export const getInitialResultSheetId = (location: Location = window.location) => {
    const {pathname} = location
    const {isOrderResult, isOrderSheetSellerResult} = getPathNameInfo(pathname)

    if (isOrderResult()) {
        return pathname.replace(PathnameRegexp.ORDER_RESULT, '').split('/')[0] ?? ''
    } else if (isOrderSheetSellerResult()) {
        return pathname.replace(PathnameRegexp.ORDER_SHEET_SELLER_RESULT, '').split('/')[0] ?? ''
    } else {
        return ''
    }
}

/**
 * @deprecated @web/slices/common 으로 이관
 */
export const getPageSetByPathname = (location: string = window.location.pathname) => {
    const {
        isOrderSheetApp,
        isMallCheckout,
        isEasyBookCheckout,
        isSellerCheckout,
        isFamilyPayCheckout,
        isMallResult,
        isEasyBookResult,
        isSellerResult,
        isOrderDetail,
        isPurchaseConfirmation,
        isDiconAuto,
        isPartnersAuto,
        isDiconBuyingPathname,
        isPartnersPathname,
        isPaymentDetail,
        isReceivePoint,
        isShoppingSubscription,
        isNaverFinancialAuto,
        isNaverFinancialPayment,
        isPaymentMethodManagement,
        isSaleHistory,
        isSaleHistoryDetail,
        isSaleSettlement,
        isSaleSettlementDetail,
        isNpayBizPaymentCheckout,
        isInvoice,
    } = getPathNameInfo(location)

    if (isOrderSheetApp()) {
        if (isMallCheckout()) {
            return PageSet.MALL_CHECKOUT
        } else if (isEasyBookCheckout()) {
            return PageSet.EASY_BOOK_CHECKOUT
        } else if (isSellerCheckout()) {
            return PageSet.SELLER_CHECKOUT
        } else if (isFamilyPayCheckout()) {
            return PageSet.FAMILY_PAY_CHECKOUT
        } else if (isMallResult()) {
            return PageSet.MALL_RESULT
        } else if (isEasyBookResult()) {
            return PageSet.EASY_BOOK_RESULT
        } else if (isSellerResult()) {
            return PageSet.SELLER_RESULT
        } else if (isOrderDetail()) {
            return PageSet.ORDER_DETAIL
        } else if (isPurchaseConfirmation()) {
            return PageSet.PURCHASE_CONFIRMATION
        } else if (isShoppingSubscription()) {
            return PageSet.SHOPPING_SUBSCRIPTION
        }
    }

    if (isDiconAuto()) {
        return PageSet.DICON_AUTO
    }

    if (isPartnersAuto()) {
        return PageSet.PARTNERS_AUTO
    }

    if (isDiconBuyingPathname()) {
        return PageSet.DICON_PAYMENT
    }

    if (isPartnersPathname()) {
        return PageSet.PARTNERS_PAYMENT
    }

    if (isPaymentDetail()) {
        return PageSet.PAYMENT_DETAIL
    }

    if (isReceivePoint()) {
        return PageSet.RECEIVE_POINT
    }

    if (isNaverFinancialAuto()) {
        return PageSet.NAVER_FINANCIAL_AUTO
    }

    if (isNaverFinancialPayment()) {
        return PageSet.NAVER_FINANCIAL_PAYMENT
    }

    if (isPaymentMethodManagement()) {
        return PageSet.PAYMENT_METHOD_MANAGEMENT
    }

    if (isSaleHistory()) {
        return PageSet.SALE_HISTORY
    }

    if (isSaleHistoryDetail()) {
        return PageSet.SALE_HISTORY_DETAIL
    }

    if (isSaleSettlement()) {
        return PageSet.SALE_SETTLEMENT
    }

    if (isSaleSettlementDetail()) {
        return PageSet.SALE_SETTLEMENT_DETAIL
    }

    if (isNpayBizPaymentCheckout()) {
        return PageSet.NPAY_BIZ_CHECKOUT
    }

    if (isInvoice()) {
        return PageSet.INVOICE
    }

    return null
}

/**
 * @deprecated @web/slices/common 에서만 사용
 */
export const getEntryFromPathname = (pathname: string = window.location.pathname) => {
    if (PathnameRegexp.INVOICE.test(pathname)) {
        return Entry.INVOICE
    }

    return null
}
